import type { Lead } from "@/mocks/leadsData";

type Props = {
  lead: Lead;
};

export function ContactInfoCard({ lead }: Props) {
  const createdDate = lead.createdAt
    ? new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  const lastActivityDate = lead.lastActivity
    ? new Date(lead.lastActivity).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const items = [
    { icon: "ri-phone-line text-gray-400", label: "Phone", value: lead.phone || "—" },
    { icon: "ri-mail-line text-gray-400", label: "Email", value: lead.email || "—" },
    { icon: "ri-building-line text-gray-400", label: "Company", value: lead.company || "—" },
    { icon: "ri-briefcase-line text-gray-400", label: "Company Type", value: lead.industry || "—" },
    { icon: "ri-money-rupee-circle-line text-gray-400", label: "Deal Value", value: lead.value || "₹0" },
    { icon: "ri-calendar-line text-gray-400", label: "Created", value: createdDate },
    { icon: "ri-time-line text-gray-400", label: "Last Activity", value: lastActivityDate },
  ];

  const assignedInitials = lead.assigned
    ? lead.assigned
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  const customFields = lead.customFields || (lead as any).custom_fields || {};
  const extraFields = lead.extraFields || (lead as any).extra_fields || {};
  const allCustomFields = { ...customFields, ...extraFields };
  const customEntries = Object.entries(allCustomFields).filter(
    ([, val]) => val !== undefined && val !== null && val !== ""
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Contact Information</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
              <i className={item.icon} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase text-gray-500 tracking-wider">{item.label}</p>
              <p className="text-sm text-gray-900 font-medium mt-0.5 truncate">{item.value}</p>
            </div>
          </div>
        ))}
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-line text-gray-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase text-gray-500 tracking-wider">Assigned To</p>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                {assignedInitials}
              </div>
              <p className="text-sm text-gray-900 font-medium truncate">{lead.assigned || "Unassigned"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Custom Fields Display */}
      {customEntries.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-list-check-2 text-xs text-indigo-600" />
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
              Additional Custom Fields
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {customEntries.map(([k, v]) => (
              <div key={k} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-indigo-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase text-gray-500 tracking-wider">{k}</p>
                  <p className="text-sm text-gray-900 font-medium mt-0.5">
                    {typeof v === "boolean"
                      ? v
                        ? "Yes"
                        : "No"
                      : Array.isArray(v)
                      ? v.join(", ")
                      : String(v)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}